import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Collapse,
  ListSubheader
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TextSnippetOutlined as SnippetsIcon,
  BarChart as StatsIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  TextSnippetOutlined as SnippetsHeaderIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import { useSnackbar } from '../../CentralSnackbar';

const SnippetsManagement = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useSnackbar();
  const [snippets, setSnippets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogError, setDialogError] = useState(''); // Lokaler Fehler nur für Dialog-Validierung
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snippetToDelete, setSnippetToDelete] = useState(null);
  
  // Selection states
  const [selectedSnippets, setSelectedSnippets] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    label: '',
    name: '',
    content: '',
    valid_for_users: null,
    valid_for_group: null,
    valid_for_groups: null
  });

  // Load data on component mount
  useEffect(() => {
    loadSnippets();
    loadUsers();
  }, []);

  // Selection logic
  const handleSelectSnippet = (snippetId) => {
    setSelectedSnippets(prev => 
      prev.includes(snippetId) 
        ? prev.filter(id => id !== snippetId)
        : [...prev, snippetId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSnippets([]);
    } else {
      setSelectedSnippets(snippets.map(snippet => snippet.id));
    }
    setSelectAll(!selectAll);
  };

  const hasSelected = selectedSnippets.length > 0;
  const exactlyOneSelected = selectedSnippets.length === 1;

  const loadSnippets = async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/snippets/', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSnippets(data);
      } else {
        showError(t('admin.reportingManagement.snippets.loadError'), 'ERR-SNIPPETMGMT-001');
      }
    } catch (err) {
      console.error('Network error loading snippets:', err);
      showError(t('admin.reportingManagement.snippets.loadError'), 'ERR-SNIPPETMGMT-002');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiFetch('/users/', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleAddSnippet = () => {
    setEditingSnippet(null);
    setFormData({
      label: '',
      name: '',
      content: '',
      valid_for_users: null,
      valid_for_group: null,
      valid_for_groups: null
    });
    setDialogOpen(true);
  };

  const handleEditSnippet = (snippet) => {
    setEditingSnippet(snippet);
    
    // Smart loading: Wenn Gruppen gesetzt sind, entsprechende User auch hinzufügen
    let validForUsers = snippet.valid_for_users;
    
    if (snippet.valid_for_groups) {
      const groupLevels = snippet.valid_for_groups.split(',').map(g => parseInt(g));
      const allGroupUserIds = [];
      
      groupLevels.forEach(groupLevel => {
        const groupUsers = users.filter(user => user.group_level === groupLevel);
        groupUsers.forEach(user => {
          if (!allGroupUserIds.includes(user.id)) {
            allGroupUserIds.push(user.id);
          }
        });
      });
      
      // Bestehende User-IDs mit Gruppen-User-IDs kombinieren
      const existingUserIds = validForUsers ? validForUsers.split(',').map(id => parseInt(id)) : [];
      const combinedUserIds = [...new Set([...existingUserIds, ...allGroupUserIds])];
      validForUsers = combinedUserIds.length > 0 ? combinedUserIds.join(',') : null;
    }
    
    setFormData({
      label: snippet.label,
      name: snippet.name,
      content: snippet.content,
      valid_for_users: validForUsers,
      valid_for_group: snippet.valid_for_group,
      valid_for_groups: snippet.valid_for_groups
    });
    setDialogOpen(true);
  };

  const handleSaveSnippet = async () => {
    setDialogError('');
    
    // Client-side Validierung - bleibt im Dialog
    if (!formData.label || !formData.name || !formData.content) {
      setDialogError(t('admin.reportingManagement.snippets.requiredFields'));
      return;
    }

    // Validate label format (allow German umlauts: ÄÖÜ)
    if (!formData.label.match(/^[A-ZÄÖÜ0-9-]+$/)) {
      setDialogError(t('admin.reportingManagement.snippets.invalidLabel'));
      return;
    }

    try {
      const url = editingSnippet ? `/snippets/${editingSnippet.id}` : '/snippets/';
      const method = editingSnippet ? 'PUT' : 'POST';
      
      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // Erfolg: Dialog schließen, dann Snackbar auf Hauptseite zeigen
        setDialogOpen(false);
        showSuccess(editingSnippet 
          ? t('admin.reportingManagement.snippets.updateSuccess')
          : t('admin.reportingManagement.snippets.createSuccess')
        );
        loadSnippets();
      } else {
        // Backend-Fehler (z.B. Duplikat): Im Dialog anzeigen
        const errorData = await response.json();
        setDialogError(errorData.detail || t('admin.reportingManagement.snippets.saveError'));
        console.error('Backend error saving snippet:', errorData);
        showError(errorData.detail || t('admin.reportingManagement.snippets.saveError'), 'ERR-SNIPPETMGMT-003');
      }
    } catch (err) {
      console.error('Network error saving snippet:', err);
      showError(t('admin.reportingManagement.snippets.saveError'), 'ERR-SNIPPETMGMT-004');
    }
  };

  const handleOpenEdit = () => {
    if (exactlyOneSelected) {
      const snippet = snippets.find(s => s.id === selectedSnippets[0]);
      handleEditSnippet(snippet);
    }
  };

  const handleDeleteSelected = () => {
    if (hasSelected) {
      setDeleteDialogOpen(true);
    }
  };

  const confirmDeleteSnippet = async () => {
    try {
      // Delete all selected snippets
      for (const snippetId of selectedSnippets) {
        const response = await apiFetch(`/snippets/${snippetId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          console.error(`Failed to delete snippet ${snippetId}`);
          showError(t('admin.reportingManagement.snippets.deleteError'), 'ERR-SNIPPETMGMT-005');
          return;
        }
      }

      // Erfolg: Dialog schließen, dann Snackbar zeigen
      setDeleteDialogOpen(false);
      const count = selectedSnippets.length;
      showSuccess(count === 1 
        ? t('admin.reportingManagement.snippets.deleteSuccess')
        : t('admin.reportingManagement.snippets.deleteMultipleSuccess', { count })
      );
      setSelectedSnippets([]);
      setSelectAll(false);
      loadSnippets();
    } catch (err) {
      console.error('Network error deleting snippets:', err);
      showError(t('admin.reportingManagement.snippets.deleteError'), 'ERR-SNIPPETMGMT-006');
    }
  };

  const getPermissionDisplay = (snippet) => {
    if (!snippet.valid_for_users && !snippet.valid_for_group && !snippet.valid_for_groups) {
      return (
        <Chip 
          icon={<PublicIcon />} 
          label={t('admin.reportingManagement.snippets.forEveryone')} 
          color="primary" 
          size="small"
        />
      );
    }
    
    const chips = [];
    
    // Neue Mehrfach-Gruppen (priorisiert)
    if (snippet.valid_for_groups) {
      const groupLevels = snippet.valid_for_groups.split(',').map(g => parseInt(g));
      groupLevels.forEach(groupLevel => {
        chips.push(
          <Chip 
            key={`group-${groupLevel}`}
            icon={<GroupIcon />} 
            label={t(`admin.userManagement.userDialog.groups.${groupLevel}`)} 
            color="secondary" 
            size="small"
          />
        );
      });
    }
    // Legacy single group (fallback)
    else if (snippet.valid_for_group !== null) {
      chips.push(
        <Chip 
          key="group"
          icon={<GroupIcon />} 
          label={t(`admin.userManagement.userDialog.groups.${snippet.valid_for_group}`)} 
          color="secondary" 
          size="small"
        />
      );
    }
    
    if (snippet.valid_for_users) {
      const userCount = snippet.valid_for_users.split(',').length;
      chips.push(
        <Chip 
          key="users"
          icon={<PersonIcon />} 
          label={t('admin.reportingManagement.snippets.forSpecificUsers', { count: userCount })} 
          color="info" 
          size="small"
        />
      );
    }
    
    return <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{chips}</Box>;
  };

  const selectedUserIds = formData.valid_for_users ? formData.valid_for_users.split(',').map(id => parseInt(id)) : [];
  const selectedUsers = users.filter(user => selectedUserIds.includes(user.id));
  
  // Selected Groups Logic - Echte Mehrfachauswahl
  const selectedGroups = formData.valid_for_groups 
    ? formData.valid_for_groups.split(',').map(g => parseInt(g))
    : [];

  const handleGroupToggle = (groupLevel) => {
    const isSelected = selectedGroups.includes(groupLevel);
    let newSelectedGroups;
    let newSelectedUsers = [...selectedUsers];
    
    if (isSelected) {
      // Gruppe entfernen
      newSelectedGroups = selectedGroups.filter(g => g !== groupLevel);
      // Alle User dieser Gruppe aus der Auswahl entfernen
      const groupUsers = users.filter(user => user.group_level === groupLevel);
      newSelectedUsers = newSelectedUsers.filter(selected => 
        !groupUsers.some(groupUser => groupUser.id === selected.id)
      );
    } else {
      // Gruppe hinzufügen
      newSelectedGroups = [...selectedGroups, groupLevel];
      // Alle User dieser Gruppe zur Auswahl hinzufügen
      const groupUsers = users.filter(user => user.group_level === groupLevel);
      groupUsers.forEach(groupUser => {
        if (!newSelectedUsers.some(selected => selected.id === groupUser.id)) {
          newSelectedUsers.push(groupUser);
        }
      });
    }
    
    // Als komma-getrennte Listen speichern
    const groupsValue = newSelectedGroups.length > 0 ? newSelectedGroups.join(',') : null;
    const usersValue = newSelectedUsers.length > 0 ? newSelectedUsers.map(u => u.id).join(',') : null;
    
    setFormData(prev => ({ 
      ...prev, 
      valid_for_groups: groupsValue,
      valid_for_users: usersValue,
      valid_for_group: null // Legacy field auf null setzen
    }));
  };

  const handleUserToggle = (user) => {
    const isSelected = selectedUsers.some(selected => selected.id === user.id);
    const newSelectedUsers = isSelected
      ? selectedUsers.filter(selected => selected.id !== user.id)
      : [...selectedUsers, user];

    // Prüfen welche Gruppen noch vollständig ausgewählt sind
    let newSelectedGroups = [...selectedGroups];
    
    // Für jede aktuell ausgewählte Gruppe prüfen
    selectedGroups.forEach(groupLevel => {
      const groupUsers = users.filter(u => u.group_level === groupLevel);
      const allGroupUsersSelected = groupUsers.every(groupUser => 
        newSelectedUsers.some(selectedUser => selectedUser.id === groupUser.id)
      );
      
      // Wenn nicht mehr alle User der Gruppe ausgewählt sind, Gruppe entfernen
      if (!allGroupUsersSelected) {
        newSelectedGroups = newSelectedGroups.filter(g => g !== groupLevel);
      }
    });
    
    // Prüfen ob eine neue Gruppe vollständig ausgewählt wurde
    [0, 1, 2, 3, 4, 5].forEach(groupLevel => {
      if (!newSelectedGroups.includes(groupLevel)) {
        const groupUsers = users.filter(u => u.group_level === groupLevel);
        if (groupUsers.length > 0) {
          const allGroupUsersSelected = groupUsers.every(groupUser => 
            newSelectedUsers.some(selectedUser => selectedUser.id === groupUser.id)
          );
          
          if (allGroupUsersSelected) {
            newSelectedGroups.push(groupLevel);
          }
        }
      }
    });

    const userIds = newSelectedUsers.map(u => u.id).join(',');
    const groupsValue = newSelectedGroups.length > 0 ? newSelectedGroups.join(',') : null;
    
    setFormData(prev => ({ 
      ...prev, 
      valid_for_users: userIds || null,
      valid_for_groups: groupsValue
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SnippetsHeaderIcon color="primary" />
          {t('admin.reportingManagement.snippets.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddSnippet}
          className="button"
        >
          {t('admin.reportingManagement.snippets.addSnippet')}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.reportingManagement.snippets.description')}
      </Typography>

      {/* Aktionsleiste */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ mr: 2 }}>
              {hasSelected
                ? t('admin.reportingManagement.snippets.selected', { count: selectedSnippets.length })
                : t('admin.reportingManagement.snippets.noSelected')
              }
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={t('admin.reportingManagement.snippets.editSnippet')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenEdit}
                  disabled={!exactlyOneSelected || loading}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('admin.reportingManagement.snippets.deleteSnippet')}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDeleteSelected}
                  disabled={!hasSelected || loading}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Paper>
      ) : (
        <Paper sx={{ width: '100%' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      disabled={snippets.length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.snippets.label')}</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.snippets.name')}</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.snippets.content')}</TableCell>
                  <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.snippets.permissions')}</TableCell>
                  <TableCell align="center" sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                    <StatsIcon sx={{ verticalAlign: 'middle' }} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snippets.map((snippet) => (
                  <TableRow 
                    key={snippet.id}
                    selected={selectedSnippets.includes(snippet.id)}
                    hover
                    onClick={() => handleSelectSnippet(snippet.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSnippets.includes(snippet.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectSnippet(snippet.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontFamily: 'monospace', 
                        backgroundColor: (theme) => theme.palette.action.hover, 
                        padding: '2px 4px', 
                        borderRadius: 1 
                      }}>
                        @{snippet.label}
                      </Typography>
                    </TableCell>
                    <TableCell>{snippet.name}</TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 300, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={snippet.content}
                      >
                        {snippet.content}
                      </Typography>
                    </TableCell>
                    <TableCell>{getPermissionDisplay(snippet)}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {snippet.used_x_times || 0}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {snippets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {t('admin.reportingManagement.snippets.noSnippets')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingSnippet 
            ? t('admin.reportingManagement.snippets.editSnippet')
            : t('admin.reportingManagement.snippets.addSnippet')
          }
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
              {dialogError}
            </Alert>
          )}
          
          <Box sx={{ mt: 1 }}>
            {/* Erste Zeile: Label (SCHMAL) und Name (BREIT) */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ width: '25%' }}>
                <TextField
                  fullWidth
                  label={t('admin.reportingManagement.snippets.label')}
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value.toUpperCase() })}
                  placeholder="TEXT-1234"
                  helperText={t('admin.reportingManagement.snippets.labelHelp')}
                  inputProps={{ pattern: '[A-ZÄÖÜ0-9-]+' }}
                  required
                />
              </Box>
              <Box sx={{ width: '75%' }}>
                <TextField
                  fullWidth
                  label={t('admin.reportingManagement.snippets.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('admin.reportingManagement.snippets.namePlaceholder')}
                  required
                />
              </Box>
            </Box>

            {/* Zweite Zeile: Content (ganze Breite) */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label={t('admin.reportingManagement.snippets.content')}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder={t('admin.reportingManagement.snippets.contentPlaceholder')}
                required
              />
            </Box>

            {/* Permissions Bereich */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('admin.reportingManagement.snippets.permissions')}
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('admin.reportingManagement.snippets.permissionInfo')}
            </Alert>

            {/* Groups und Users nebeneinander - jeweils 500px breit */}
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
              {/* Groups Liste links */}
              <Box sx={{ width: '500px' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {t('admin.reportingManagement.snippets.validForGroup')}
                </Typography>
                <Paper variant="outlined" sx={{ height: 320, overflow: 'auto' }}>
                  <List dense>
                    {[0, 1, 2, 3, 4, 5].map((groupLevel) => {
                      const isSelected = selectedGroups.includes(groupLevel);
                      return (
                        <ListItem
                          key={groupLevel}
                          dense
                          button
                          onClick={() => handleGroupToggle(groupLevel)}
                        >
                          <Checkbox
                            edge="start"
                            checked={isSelected}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText 
                            primary={t(`admin.userManagement.userDialog.groups.${groupLevel}`)}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              </Box>

              {/* Users Liste rechts */}
              <Box sx={{ width: '500px' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {t('admin.reportingManagement.snippets.validForUsers')}
                </Typography>
                <Paper variant="outlined" sx={{ height: 320, overflow: 'auto' }}>
                  <List dense>
                    {users
                      .filter(user => (user.group_level || 0) !== 6)
                      .sort((a, b) => (a.group_level || 0) - (b.group_level || 0))
                      .reduce((acc, user) => {
                        const groupLevel = user.group_level || 0;
                        const lastGroup = acc[acc.length - 1];
                        
                        if (!lastGroup || lastGroup.groupLevel !== groupLevel) {
                          acc.push({
                            groupLevel,
                            users: [user],
                            isGroup: true
                          });
                        } else {
                          lastGroup.users.push(user);
                        }
                        return acc;
                      }, [])
                      .map((group, groupIndex) => (
                        <React.Fragment key={group.groupLevel}>
                          <ListSubheader 
                            component="div" 
                            sx={{ 
                              bgcolor: 'action.hover',
                              position: 'relative',
                              zIndex: 1
                            }}
                            disableSticky
                          >
                            {t(`admin.userManagement.userDialog.groups.${group.groupLevel}`)}
                          </ListSubheader>
                          {group.users.map((user) => {
                            const isSelected = selectedUsers.some(selected => selected.id === user.id);
                            return (
                              <ListItem
                                key={user.id}
                                dense
                                button
                                onClick={() => handleUserToggle(user)}
                              >
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  tabIndex={-1}
                                  disableRipple
                                />
                                <ListItemText 
                                  primary={user.full_name || user.username}
                                  secondary={user.username !== user.full_name ? user.username : null}
                                />
                              </ListItem>
                            );
                          })}
                        </React.Fragment>
                      ))
                    }
                  </List>
                </Paper>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t('admin.reportingManagement.snippets.cancel')}
          </Button>
          <Button 
            onClick={handleSaveSnippet} 
            variant="contained" 
            className="button"
          >
            {editingSnippet 
              ? t('admin.reportingManagement.snippets.updateSnippet')
              : t('admin.reportingManagement.snippets.createSnippet')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {selectedSnippets.length === 1
            ? t('admin.reportingManagement.snippets.deleteConfirm.title')
            : t('admin.reportingManagement.snippets.deleteMultiple.title')
          }
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedSnippets.length === 1
              ? t('admin.reportingManagement.snippets.deleteConfirm.message', { 
                  name: snippets.find(s => s.id === selectedSnippets[0])?.name,
                  label: snippets.find(s => s.id === selectedSnippets[0])?.label 
                })
              : t('admin.reportingManagement.snippets.deleteMultiple.message', { 
                  count: selectedSnippets.length 
                })
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('admin.reportingManagement.snippets.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteSnippet} 
            color="error" 
            variant="contained"
          >
            {t('admin.reportingManagement.snippets.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnippetsManagement;
